/*----------------------------------
- CATALOG (SSOT): HOME FUNNEL
----------------------------------*/

/**
 * "We monitor every one-word domain" (docs/mockups/website-home-v3.html, `section#how`): the twelve kinds of source the
 * scanner reads, the ten checks a name goes through again, and the sentence under each stage's figure. The figures are
 * never here: the page formats the server's `totalDataPoints`, `totalDomains` and `checkedLast24h` (Home's
 * `LandingStats`), and the last stage shows the shipped sample label, marked as one below.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule No figure is computed in the client (Domain Data Ownership). The mock's "real words kept" count does not exist
 *       in the scanner, so stage 2 reads the catalog size (`totalDomains`, the one-word names kept) and nothing else.
 * @rule No source is named: the kinds are categories ("Sales record", "Marketplaces"), never a venue or a feed.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { SpriteIconName } from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

/** A source kind: its icon on a glyph tinted by a system token (the mock's `--ud-seed` per source). */
export type HomeFunnelSource = { label: string; icon: SpriteIconName; seed: string };

export type HomeFunnelCheck = { label: string; icon: SpriteIconName };

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const HOME_FUNNEL_HEADLINE = 'We monitor every one-word domain';

/** The mock's lucide icons, each in the website sprite under its own name (`build-icon-sprite.mjs`, mock parity web-frame-13). */
export const HOME_FUNNEL_SOURCES: readonly HomeFunnelSource[] = [
    { label: 'Registries', icon: 'database', seed: 'var(--ud-viz-2)' },
    { label: 'DNS', icon: 'globe', seed: 'var(--ud-dormant)' },
    { label: 'Registrars', icon: 'tag', seed: 'var(--ud-alert)' },
    { label: 'Marketplaces', icon: 'store', seed: 'var(--ud-premium)' },
    { label: 'Auctions', icon: 'gavel', seed: 'var(--ud-auction)' },
    { label: 'Drop lists', icon: 'droplet', seed: 'var(--ud-premium-ink)' },
    { label: 'Sales record', icon: 'coins', seed: 'var(--ud-positive)' },
    { label: 'Search demand', icon: 'search', seed: 'var(--ud-viz-2)' },
    { label: 'Trends', icon: 'trending-up', seed: 'var(--ud-positive)' },
    { label: 'Trademarks', icon: 'shield-check', seed: 'var(--ud-danger-ink)' },
    { label: 'Web history', icon: 'clock', seed: 'var(--ud-graphite)' },
    { label: 'Dictionaries', icon: 'book-open', seed: 'var(--ud-viz-1)' },
];

export const HOME_FUNNEL_CHECKS: readonly HomeFunnelCheck[] = [
    { label: 'Status', icon: 'clock' },
    { label: 'DNS', icon: 'globe' },
    { label: 'Price now', icon: 'tag' },
    { label: 'Renewal', icon: 'coins' },
    { label: 'Listings', icon: 'store' },
    { label: 'Auctions', icon: 'gavel' },
    { label: 'Sales', icon: 'chart-bar' },
    { label: 'Demand', icon: 'search' },
    { label: 'Trademark', icon: 'shield-check' },
    { label: 'History', icon: 'link' },
];

/** The sentence under each stage's figure, in the stages' order. */
export const HOME_FUNNEL_TEXT = {
    sources: 'data points read from these sources.',
    kept: 'real one-word names kept. Digits, hyphens and non-words never reach your list.',
    checked: 'names checked again in the last 24 hours, the words in demand most often.',
    sample: 'new names this morning for your search, each with a rating and the reason behind it.',
} as const;

/**
 * SAMPLE, not a server figure: the last stage illustrates one search's morning ("8 new names"). It is the mock's shipped
 * label, kept as a sample on purpose because a search's matches are per member and never reach a public page.
 */
export const HOME_FUNNEL_SAMPLE_LABEL = '8';

/** The checks' accessible name: the rising list itself is hidden from assistive tech. */
export const buildHomeFunnelChecksLabel = (checks: readonly HomeFunnelCheck[]): string =>
    `Each name is checked for ${checks.map((check) => check.label.toLowerCase()).join(', ')}`;
